
import React from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';

interface DesktopPepitesProps {
    data: any;
    activeTab: string;
    setActiveTab: (tab: any) => void;
    filters: any;
    setFilters: (filters: any) => void;
    opportunities: any[];
    sellSignals: any[];
    filteredUniverse: any[];
    sentiment: string;
    sentimentColors: any;
    navigate: (path: string) => void;
}

const DesktopPepites: React.FC<DesktopPepitesProps> = ({
    data,
    activeTab,
    setActiveTab,
    filters,
    setFilters,
    opportunities,
    sellSignals,
    filteredUniverse,
    sentiment,
    sentimentColors,
    navigate
}) => {
    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <Disclaimer type="investment" />

            {/* Simplified Summary */}
            <Box>
                <div style={styles.summaryGrid}>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>🚀 Pépites détectées</span>
                        <span style={styles.summaryValue}>{opportunities.length}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>📈 Sentiment marché</span>
                        <span style={{
                            ...styles.badge,
                            backgroundColor: sentimentColors[sentiment].bg,
                            color: sentimentColors[sentiment].color,
                        }}>
                            {sentiment === 'BULLISH' ? '🟢 Haussier' : sentiment === 'BEARISH' ? '🔴 Baissier' : '⚪ Neutre'}
                        </span>
                    </div>
                </div>
            </Box>

            {/* Tabs */}
            <div style={styles.tabContainer}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'opportunities' ? styles.tabActive : {}) }}
                    onClick={() => setActiveTab('opportunities')}
                >
                    🔥 Configurations Haussières ({opportunities.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'sell' ? styles.tabActiveSell : {}) }}
                    onClick={() => setActiveTab('sell')}
                >
                    📉 Alertes Baissières ({sellSignals.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'universe' ? styles.tabActiveUniverse : {}) }}
                    onClick={() => setActiveTab('universe')}
                >
                    🌍 Univers ({filteredUniverse.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActiveInfo : {}) }}
                    onClick={() => setActiveTab('info')}
                >
                    ℹ️ Stratégie Applicable
                </button>
            </div>

            {/* Opportunities */}
            {activeTab === 'opportunities' && (
                <div style={styles.cardsGrid}>
                    {opportunities.length === 0 ? (
                        <Box>
                            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p>Aucune action en Breakout (CCI &gt; 100) actuellement.</p>
                                <p style={{ fontSize: 14 }}>Les opportunités de ce type sont rares et explosives.</p>
                            </div>
                        </Box>
                    ) : (
                        opportunities.map((opp) => (
                            <Box key={opp.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${opp.symbole}`)}>
                                                {opp.symbole}
                                            </span>
                                            <p style={styles.companyName}>{opp.nom_societe}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ ...styles.scoreBadge, backgroundColor: '#7c3aed' }}>
                                                {opp.score}
                                            </div>
                                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Score</span>
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div>
                                            <span style={styles.priceLabel}>Prix Actuel</span>
                                            <span style={styles.priceValue}>{opp.entry_price.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>CCI</span>
                                            <span style={{ ...styles.priceValue, color: (opp.cci || 0) > 100 ? '#16a34a' : 'var(--text-main)' }}>
                                                {opp.cci?.toFixed(0)}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Pression</span>
                                            <span style={{ ...styles.priceValue, color: (opp.current_pression || 0) > 1.2 ? '#16a34a' : 'var(--text-muted)' }}>
                                                {opp.current_pression?.toFixed(1) || '-'}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Vol. Échangé</span>
                                            <span style={{ ...styles.priceValue, color: (opp.current_ve || 0) > 2000000 ? '#16a34a' : 'var(--text-muted)' }}>
                                                {(opp.current_ve ? (opp.current_ve / 1000000).toFixed(1) : '0')}M
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Z-Score</span>
                                            <span style={{ ...styles.priceValue, color: (opp.z_score || 0) > 2.0 ? '#16a34a' : 'var(--text-main)' }}>
                                                {opp.z_score?.toFixed(2)}σ
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>OBV Trend</span>
                                            <span style={{ ...styles.priceValue, color: opp.obv_trend === 'BULLISH' ? '#16a34a' : 'var(--text-muted)' }}>
                                                {opp.obv_trend === 'BULLISH' ? '↗️ HAUSSIER' : 'NEUTRE'}
                                            </span>
                                        </div>
                                    </div>


                                    <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {opp.volume_spike && opp.volume_spike > 1.5 && (
                                            <span style={{ fontSize: 12, backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#d97706', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                                ⚡ Volume x{opp.volume_spike.toFixed(1)}
                                            </span>
                                        )}
                                        <span style={{ fontSize: 12, backgroundColor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                            🟢 Tendance {opp.trend_days !== undefined ? `${opp.trend_days}j` : 'récente'}
                                        </span>
                                    </div>

                                    <p style={styles.justification}>{opp.justification}</p>
                                </div>
                            </Box>
                        )
                    ))}
                </div>
            )}

            {/* Sell Signals */}
            {activeTab === 'sell' && (
                <div style={styles.cardsGrid}>
                    {sellSignals.length === 0 ? (
                        <Box>
                            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p>Aucune position actuellement détenue pour cette stratégie.</p>
                                <p style={{ fontSize: 14 }}>Les alertes d'essoufflement apparaîtront ici quand CCI &lt; 100.</p>
                            </div>
                        </Box>
                    ) : (
                        sellSignals.map((signal: any) => (
                            <Box key={signal.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${signal.symbole}`)}>
                                                {signal.symbole}
                                            </span>
                                            <p style={styles.companyName}>{signal.nom_societe}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ ...styles.scoreBadge, backgroundColor: signal.signal_strength === 'STRONG' ? '#dc2626' : '#f59e0b' }}>
                                                {signal.signal_strength === 'STRONG' ? '🔴' : '🟡'}
                                            </div>
                                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{signal.signal_type}</span>
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div>
                                            <span style={styles.priceLabel}>Prix Actuel</span>
                                            <span style={styles.priceValue}>{signal.current_price?.toLocaleString()}</span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>P&L</span>
                                            <span style={{ ...styles.priceValue, color: signal.unrealized_pnl_pct >= 0 ? '#16a34a' : 'var(--text-danger, #dc2626)' }}>
                                                {signal.unrealized_pnl_pct >= 0 ? '+' : ''}{signal.unrealized_pnl_pct?.toFixed(2)}%
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>CCI</span>
                                            <span style={{ ...styles.priceValue, color: (signal.cci || 0) > 100 ? '#16a34a' : 'var(--text-danger, #dc2626)' }}>
                                                {signal.cci?.toFixed(0)}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Z-Score</span>
                                            <span style={{ ...styles.priceValue, color: (signal.z_score || 0) > 0 ? '#16a34a' : 'var(--text-danger, #dc2626)' }}>
                                                {signal.z_score?.toFixed(2)}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>OBV</span>
                                            <span style={{ ...styles.priceValue, color: signal.obv === 'BULLISH' ? '#16a34a' : 'var(--text-danger, #dc2626)' }}>
                                                {signal.obv === 'BULLISH' ? '↗️' : '↘️'}
                                            </span>
                                        </div>
                                    </div>

                                    <p style={styles.justification}>{signal.justification}</p>

                                    <div style={styles.actionRow}>
                                        <span style={{ ...styles.actionBadge, backgroundColor: 'var(--text-danger, #dc2626)', color: 'white' }}>
                                            ALERTE BAISSIÈRE
                                        </span>
                                    </div>
                                </div>
                            </Box>
                        )
                    ))}
                </div>
            )}

            {/* Universe */}
            {activeTab === 'universe' && (
                <Box>
                    {/* Filters UI */}
                    <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-input)', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                            <div style={{ flex: 1, minWidth: 200, maxWidth: 300 }}>
                                <input
                                    type="text"
                                    placeholder="🔍 Rechercher..."
                                    value={filters.search}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                    style={styles.searchInput}
                                />
                            </div>
                            <div style={{ height: 24, borderLeft: '1px solid var(--border-color)', margin: '0 8px' }} />

                            <button
                                onClick={() => setFilters({ ...filters, cciBreakout: !filters.cciBreakout })}
                                style={{
                                    ...styles.filterChip,
                                    ...(filters.cciBreakout ? styles.filterChipActive : {})
                                }}
                            >
                                {filters.cciBreakout ? '✅' : '⬜'} CCI {'>'} 100
                            </button>

                            <button
                                onClick={() => setFilters({ ...filters, zScoreExtreme: !filters.zScoreExtreme })}
                                style={{
                                    ...styles.filterChip,
                                    ...(filters.zScoreExtreme ? styles.filterChipActive : {})
                                }}
                            >
                                {filters.zScoreExtreme ? '✅' : '⬜'} Z-Score {'>'} 2.0
                            </button>

                            <button
                                onClick={() => setFilters({ ...filters, obvBullish: !filters.obvBullish })}
                                style={{
                                    ...styles.filterChip,
                                    ...(filters.obvBullish ? styles.filterChipActive : {})
                                }}
                            >
                                {filters.obvBullish ? '✅' : '⬜'} OBV Bullish
                            </button>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.universeTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Symbole</th>
                                    <th style={styles.th}>Nom</th>
                                    <th style={styles.thCenter}>Cours</th>
                                    <th style={styles.thCenter}>CCI</th>
                                    <th style={styles.thCenter}>Tendance</th>
                                    <th style={styles.thCenter}>Pression</th>
                                    <th style={styles.thCenter}>Z-Score</th>
                                    <th style={styles.thCenter}>OBV</th>
                                    <th style={styles.thCenter}>Vol Ratio</th>
                                    <th style={styles.thCenter}>Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUniverse.map((stock: any) => (
                                    <tr key={stock.symbole} style={{
                                        backgroundColor: stock.is_opportunity ? 'rgba(124, 58, 237, 0.1)' : stock.is_owned ? 'rgba(14, 165, 233, 0.1)' : 'transparent'
                                    }}>
                                        <td style={styles.td}>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${stock.symbole}`)}>
                                                {stock.symbole}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{stock.nom_societe}</td>
                                        <td style={styles.tdCenter}>{stock.cours_actuel?.toLocaleString()}</td>
                                        <td style={{ ...styles.tdCenter, fontWeight: 'bold', color: stock.cci > 100 ? '#16a34a' : stock.cci < 50 ? 'var(--text-danger, #dc2626)' : 'var(--text-main)' }}>
                                            {stock.cci?.toFixed(0)}
                                        </td>
                                        <td style={{ ...styles.tdCenter, fontSize: 11, whiteSpace: 'nowrap' }}>
                                            <span style={{
                                                padding: '2px 6px',
                                                borderRadius: 10,
                                                backgroundColor: stock.trend_status === 'active' ? 'rgba(22, 163, 74, 0.1)' : stock.trend_status === 'warning' ? 'rgba(245, 158, 11, 0.1)' : stock.trend_status === 'ended' ? 'rgba(220, 38, 38, 0.1)' : 'var(--bg-box)',
                                                color: stock.trend_status === 'active' ? '#16a34a' : stock.trend_status === 'warning' ? '#d97706' : stock.trend_status === 'ended' ? 'var(--text-danger, #dc2626)' : 'var(--text-muted)'
                                            }}>
                                                {stock.trend_status === 'active' ? '🟢' : stock.trend_status === 'warning' ? '🟡' : stock.trend_status === 'ended' ? '🔴' : '⚪'}
                                                {stock.trend_days !== undefined ? ` ${stock.trend_days}j` : ''}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.tdCenter, fontWeight: 'bold', color: (stock.current_pression || 0) > 1.2 ? '#16a34a' : (stock.current_pression || 0) < 0.8 ? 'var(--text-danger, #dc2626)' : 'var(--text-main)' }}>
                                            {stock.current_pression?.toFixed(1) || '-'}
                                        </td>
                                        <td style={{ ...styles.tdCenter, fontWeight: 'bold', color: (stock.z_score || 0) > 2.0 ? '#16a34a' : 'var(--text-main)' }}>
                                            {stock.z_score?.toFixed(2)}
                                        </td>
                                        <td style={{ ...styles.tdCenter, color: stock.obv_trend === 'BULLISH' ? '#16a34a' : 'var(--text-muted)' }}>
                                            {stock.obv_trend === 'BULLISH' ? '↗️' : '➡'}
                                        </td>
                                        <td style={styles.tdCenter}>{stock.volume_ratio?.toFixed(1)}x</td>
                                        <td style={styles.tdCenter}>
                                            {stock.is_opportunity ? '🚀 Opportunité' : stock.is_owned ? '📍 Détenu' : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Box>
            )}

            {/* Info */}
            {activeTab === 'info' && (
                <Box>
                    <div style={{ padding: 20 }}>
                        <h3>💎 Stratégie Applicable : "Trend Following"</h3>
                        <p>Cette stratégie identifie les titres entrant dans une phase d'accélération haussière durable, validée par le carnet d'ordres.</p>

                        <h4 style={{ marginTop: 20 }}>🟢 Détection de Tendance (Entrée)</h4>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 10 }}>Les 3 conditions doivent être réunies simultanément :</p>
                        <ul style={{ listStyleType: 'none', padding: 0 }}>
                            <li style={{ marginBottom: 8 }}>✅ <strong>CCI &gt; 100</strong> : L'indicateur de momentum signale une accélération haussière.</li>
                            <li style={{ marginBottom: 8 }}>✅ <strong>Pression &gt; 1.2</strong> : La demande (Quantité Achat) dépasse l'offre (Quantité Vente) de 20%.</li>
                            <li style={{ marginBottom: 8 }}>✅ <strong>Liquidité &gt; 2M FCFA</strong> : Un volume d'échange minimum valide la robustesse du mouvement.</li>
                        </ul>

                        <h4 style={{ marginTop: 20 }}>🔴 Fin de Tendance (Sortie)</h4>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 10 }}>Une seule condition suffit pour invalider la tendance :</p>
                        <ul style={{ listStyleType: 'none', padding: 0 }}>
                            <li style={{ marginBottom: 8 }}>❌ <strong>CCI &lt; 50</strong> : Le momentum s'essouffle significativement.</li>
                            <li style={{ marginBottom: 8 }}>❌ <strong>Pression &lt; 0.8</strong> : Les vendeurs reprennent le contrôle du carnet.</li>
                            <li style={{ marginBottom: 8 }}>❌ <strong>Surtension Vendeuse</strong> : La quantité à la vente double soudainement (Panic Sell).</li>
                        </ul>

                        <h4 style={{ marginTop: 20 }}>🚀 Gestion de Position</h4>
                        <ul style={{ listStyleType: 'none', padding: 0 }}>
                            <li style={{ marginBottom: 8 }}>🔹 <strong>Objectif :</strong> Illimité. On laisse courir les gains tant que le statut reste "🟢 Tendance".</li>
                            <li style={{ marginBottom: 8 }}>🔹 <strong>Stop de Protection :</strong> Sortie immédiate dès que le statut passe au rouge ou CCI &lt; 50.</li>
                        </ul>
                    </div>
                </Box>
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, padding: 20 },
    summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
    summaryLabel: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 },
    summaryValue: { fontSize: 20, fontWeight: 'bold', color: 'var(--text-heading)' },
    badge: { padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: '600' },
    tabContainer: { display: 'flex', gap: 8, marginBottom: 20, marginTop: 20 },
    tab: { flex: 1, padding: '12px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: '600', cursor: 'pointer', backgroundColor: 'var(--bg-box)', color: 'var(--text-main)' },
    tabActive: { backgroundColor: '#7c3aed', color: 'white' },
    tabActiveSell: { backgroundColor: '#dc2626', color: 'white' },
    tabActiveUniverse: { backgroundColor: '#2563eb', color: 'white' },
    tabActiveInfo: { backgroundColor: 'var(--bg-input)', color: 'var(--text-heading)' },
    cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 },
    card: { padding: 16, backgroundColor: 'var(--bg-box)', borderRadius: 12, border: '1px solid var(--border-color)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
    symbolLink: { fontSize: 18, fontWeight: 'bold', color: '#2563eb', cursor: 'pointer' },
    companyName: { fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0 0' },
    scoreBadge: { width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 16 },
    priceGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 12, marginBottom: 12, padding: 12, backgroundColor: 'var(--bg-input)', borderRadius: 8 },
    priceLabel: { display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 },
    priceValue: { display: 'block', fontSize: 14, fontWeight: '600', color: 'var(--text-heading)' },
    justification: { fontSize: 13, color: 'var(--text-muted)', margin: 0, fontStyle: 'italic' },
    actionRow: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 10 },
    actionBadge: { padding: '8px 24px', borderRadius: 6, fontSize: 13, fontWeight: 'bold', textAlign: 'center' },
    universeTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--border-color)', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    thCenter: { textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid var(--border-color)', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    td: { padding: '10px 8px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'middle', color: 'var(--text-main)' },
    tdCenter: { padding: '10px 8px', borderBottom: '1px solid var(--border-color)', textAlign: 'center', color: 'var(--text-main)' },
    searchInput: { width: '100%', padding: '8px 12px', borderRadius: 20, border: '1px solid var(--border-color)', fontSize: 13, outline: 'none', backgroundColor: 'transparent', color: 'var(--text-main)' },
    filterChip: { padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-box)', fontSize: 13, cursor: 'pointer', transition: 'all 0.2s', color: 'var(--text-muted)' },
    filterChipActive: { backgroundColor: 'rgba(37, 99, 235, 0.1)', borderColor: '#2563eb', color: '#2563eb', fontWeight: 'bold' },
};

export default DesktopPepites;
